import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import {
    CanvasPoint,
    ENTITY_NODE_HEIGHT,
    ENTITY_NODE_WIDTH,
    OwnershipFlowEdge,
    OwnershipFlowNode,
} from "./ownership.types";

const ELBOW = 18;
const ROW_CLEARANCE = 22;
const LABEL_WIDTH = 120;
const LABEL_HEIGHT = 26;
const MAX_PUSH_ITERATIONS = 12;
// Breathing room kept between a consolidation boundary and the routed edges /
// labels it must contain.
const GROUP_CONTENT_PADDING = 20;
// How far outside a node border a same-column reference loop sits.
const LOCAL_LANE_GAP = 28;
// Keep a side-attached endpoint this far inside the node's top/bottom edges.
const EDGE_INSET = 6;
// Minimum horizontal gap between two columns to thread a vertical lane between
// them; below this the edge loops just outside one side instead.
const MIN_COLUMN_GAP = 16;

export interface NodeRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

// Gap between a parent's border and the shared rail its children hang off,
// clamped to half the distance to the nearest child so the rail always sits
// between parent and child.
const BUS_GUTTER = 40;

type OwnershipDirection = "below" | "above" | "left" | "right";

interface OwnershipRoute {
    points: CanvasPoint[];
    label: CanvasPoint;
}

function dropConsecutiveDuplicates(points: CanvasPoint[]): CanvasPoint[] {
    return points.filter(
        (point, index) =>
            index === 0 ||
            point.x !== points[index - 1].x ||
            point.y !== points[index - 1].y,
    );
}

/**
 * Midpoint of the polyline's final segment — the child's incoming leg, between
 * the last turn and the arrowhead. The "%" label rides here so it's centered on
 * the approach into the child in every direction.
 */
function lastSegmentMidpoint(points: CanvasPoint[]): CanvasPoint {
    if (points.length < 2) {
        return points[points.length - 1] ?? { x: 0, y: 0 };
    }
    const a = points[points.length - 2];
    const b = points[points.length - 1];
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Dominant placement of a child relative to its parent. */
function directionOf(parent: NodeRect, child: NodeRect): OwnershipDirection {
    if (child.y >= parent.y + parent.height) {
        return "below";
    }
    if (child.y + child.height <= parent.y) {
        return "above";
    }
    return centerXOf(child) < centerXOf(parent) ? "left" : "right";
}

function routeVerticalSpine({
    parent,
    children,
    direction,
}: {
    parent: NodeRect;
    children: Array<{ edge: OwnershipFlowEdge; target: NodeRect }>;
    direction: "below" | "above";
}): Map<string, OwnershipRoute> {
    const trunkX = centerXOf(parent);
    const routes = new Map<string, OwnershipRoute>();
    if (direction === "below") {
        const parentBottom = parent.y + parent.height;
        const nearest = Math.min(...children.map(({ target }) => target.y));
        const busY =
            parentBottom + Math.min(BUS_GUTTER, (nearest - parentBottom) / 2);
        for (const { edge, target } of children) {
            const childX = centerXOf(target);
            const points = dropConsecutiveDuplicates([
                { x: trunkX, y: parentBottom },
                { x: trunkX, y: busY },
                { x: childX, y: busY },
                { x: childX, y: target.y },
            ]);
            routes.set(edge.id, { points, label: lastSegmentMidpoint(points) });
        }
        return routes;
    }
    const nearest = Math.max(
        ...children.map(({ target }) => target.y + target.height),
    );
    const busY = parent.y - Math.min(BUS_GUTTER, (parent.y - nearest) / 2);
    for (const { edge, target } of children) {
        const childX = centerXOf(target);
        const childBottom = target.y + target.height;
        const points = dropConsecutiveDuplicates([
            { x: trunkX, y: parent.y },
            { x: trunkX, y: busY },
            { x: childX, y: busY },
            { x: childX, y: childBottom },
        ]);
        routes.set(edge.id, { points, label: lastSegmentMidpoint(points) });
    }
    return routes;
}

function routeHorizontalSpine({
    parent,
    children,
    direction,
}: {
    parent: NodeRect;
    children: Array<{ edge: OwnershipFlowEdge; target: NodeRect }>;
    direction: "left" | "right";
}): Map<string, OwnershipRoute> {
    const trunkY = centerYOf(parent);
    const routes = new Map<string, OwnershipRoute>();
    if (direction === "right") {
        const parentRight = parent.x + parent.width;
        const nearest = Math.min(...children.map(({ target }) => target.x));
        const busX =
            parentRight + Math.min(BUS_GUTTER, (nearest - parentRight) / 2);
        for (const { edge, target } of children) {
            const childY = centerYOf(target);
            const points = dropConsecutiveDuplicates([
                { x: parentRight, y: trunkY },
                { x: busX, y: trunkY },
                { x: busX, y: childY },
                { x: target.x, y: childY },
            ]);
            routes.set(edge.id, { points, label: lastSegmentMidpoint(points) });
        }
        return routes;
    }
    const nearest = Math.max(
        ...children.map(({ target }) => target.x + target.width),
    );
    const busX = parent.x - Math.min(BUS_GUTTER, (parent.x - nearest) / 2);
    for (const { edge, target } of children) {
        const childY = centerYOf(target);
        const childRight = target.x + target.width;
        const points = dropConsecutiveDuplicates([
            { x: parent.x, y: trunkY },
            { x: busX, y: trunkY },
            { x: busX, y: childY },
            { x: childRight, y: childY },
        ]);
        routes.set(edge.id, { points, label: lastSegmentMidpoint(points) });
    }
    return routes;
}

/**
 * The consolidation box an ownership edge must route around: the source's group
 * box when the target is not in that same group. (Only the source-inside case
 * is handled — the common one, a group's parent entity owning a non-member.)
 */
function crossingBox(
    sourceNode: OwnershipFlowNode | undefined,
    targetNode: OwnershipFlowNode | undefined,
    rects: Map<string, NodeRect>,
): NodeRect | null {
    const sourceGroup =
        sourceNode?.type === "entityNode" ? sourceNode.parentId : undefined;
    const targetGroup =
        targetNode?.type === "entityNode" ? targetNode.parentId : undefined;
    if (sourceGroup && sourceGroup !== targetGroup) {
        return rects.get(sourceGroup) ?? null;
    }
    return null;
}

/**
 * Routes an ownership edge from a node inside a group box to one outside it so
 * the edge exits the box's nearest side and travels around the outside, rather
 * than cutting through the box interior and its dashed border. The vertical run
 * sits just beyond the box side; the edge then approaches the target's near
 * face (top when below the box, bottom when above, side when level with it).
 */
function routeAroundBox({
    source,
    target,
    box,
}: {
    source: NodeRect;
    target: NodeRect;
    box: NodeRect;
}): CanvasPoint[] {
    const side: RouteSide =
        centerXOf(target) >= centerXOf(box) ? "right" : "left";
    const exitX =
        side === "right" ? box.x + box.width + BUS_GUTTER : box.x - BUS_GUTTER;
    const sourceSideX = borderXOf(source, side);
    const sourceY = centerYOf(source);
    const targetCenterX = centerXOf(target);
    const boxTop = box.y;
    const boxBottom = box.y + box.height;

    if (target.y >= boxBottom) {
        const approachY = target.y - BUS_GUTTER;
        return dropConsecutiveDuplicates([
            { x: sourceSideX, y: sourceY },
            { x: exitX, y: sourceY },
            { x: exitX, y: approachY },
            { x: targetCenterX, y: approachY },
            { x: targetCenterX, y: target.y },
        ]);
    }
    if (target.y + target.height <= boxTop) {
        const approachY = target.y + target.height + BUS_GUTTER;
        return dropConsecutiveDuplicates([
            { x: sourceSideX, y: sourceY },
            { x: exitX, y: sourceY },
            { x: exitX, y: approachY },
            { x: targetCenterX, y: approachY },
            { x: targetCenterX, y: target.y + target.height },
        ]);
    }
    // Level with the box: approach the target from its box-facing side.
    const targetSideX = borderXOf(target, side === "right" ? "left" : "right");
    return dropConsecutiveDuplicates([
        { x: sourceSideX, y: sourceY },
        { x: exitX, y: sourceY },
        { x: exitX, y: centerYOf(target) },
        { x: targetSideX, y: centerYOf(target) },
    ]);
}

/**
 * Routes ownership edges as an org-chart "bus" that adapts to where each child
 * sits relative to its parent. Children are grouped by dominant direction
 * (below / above / left / right); each group shares one trunk out of the
 * parent's corresponding border and one rail, so every edge emanates from a
 * single point per side — the family-tree read holds no matter how nodes are
 * hand-arranged. Each "%" label rides the midpoint of the child's incoming leg.
 * Derived from the CURRENT node rects (drag-safe). Reference edges are not
 * included.
 */
export function routeOwnershipBus({
    nodes,
    edges,
}: {
    nodes: OwnershipFlowNode[];
    edges: OwnershipFlowEdge[];
}): Map<string, OwnershipRoute> {
    const rects = getAbsoluteNodeRects(nodes);
    const nodeById = new Map(nodes.map((node) => [node.id, node]));
    const routes = new Map<string, OwnershipRoute>();

    const byParent = new Map<string, OwnershipFlowEdge[]>();
    for (const edge of edges) {
        if (isReferenceEdge(edge)) {
            continue;
        }
        const group = byParent.get(edge.source) ?? [];
        group.push(edge);
        byParent.set(edge.source, group);
    }

    for (const [sourceId, groupEdges] of byParent) {
        const parent = rects.get(sourceId);
        if (!parent) {
            continue;
        }
        const byDirection: Record<
            OwnershipDirection,
            Array<{ edge: OwnershipFlowEdge; target: NodeRect }>
        > = { below: [], above: [], left: [], right: [] };
        for (const edge of groupEdges) {
            const target = rects.get(edge.target);
            if (!target) {
                continue;
            }
            // An edge leaving a group box for a node outside it routes around
            // the box instead of through it.
            const box = crossingBox(
                nodeById.get(edge.source),
                nodeById.get(edge.target),
                rects,
            );
            if (box) {
                const points = routeAroundBox({ source: parent, target, box });
                routes.set(edge.id, {
                    points,
                    label: lastSegmentMidpoint(points),
                });
                continue;
            }
            byDirection[directionOf(parent, target)].push({ edge, target });
        }

        const merge = (partial: Map<string, OwnershipRoute>) => {
            for (const [id, route] of partial) {
                routes.set(id, route);
            }
        };
        if (byDirection.below.length > 0) {
            merge(
                routeVerticalSpine({
                    parent,
                    children: byDirection.below,
                    direction: "below",
                }),
            );
        }
        if (byDirection.above.length > 0) {
            merge(
                routeVerticalSpine({
                    parent,
                    children: byDirection.above,
                    direction: "above",
                }),
            );
        }
        if (byDirection.right.length > 0) {
            merge(
                routeHorizontalSpine({
                    parent,
                    children: byDirection.right,
                    direction: "right",
                }),
            );
        }
        if (byDirection.left.length > 0) {
            merge(
                routeHorizontalSpine({
                    parent,
                    children: byDirection.left,
                    direction: "left",
                }),
            );
        }
    }
    return routes;
}

/**
 * Absolute rects for nodes (child node positions are relative to their
 * group parent in XYFlow, so parent offsets are accumulated).
 */
export function getAbsoluteNodeRects(
    nodes: OwnershipFlowNode[],
): Map<string, NodeRect> {
    const nodesById = new Map(nodes.map((node) => [node.id, node]));
    const rects = new Map<string, NodeRect>();

    for (const node of nodes) {
        let x = node.position.x;
        let y = node.position.y;
        if (node.parentId) {
            const parent = nodesById.get(node.parentId);
            if (parent) {
                x += parent.position.x;
                y += parent.position.y;
            }
        }
        rects.set(node.id, {
            x,
            y,
            width: node.width ?? ENTITY_NODE_WIDTH,
            height: node.height ?? ENTITY_NODE_HEIGHT,
        });
    }
    return rects;
}

function isReferenceEdge(edge: OwnershipFlowEdge): boolean {
    return (
        edge.data?.relationship.type !==
        ConsolidationEntityRelationshipType.ownership
    );
}

type RouteSide = "left" | "right";

function clamp(value: number, min: number, max: number): number {
    if (max < min) {
        return min;
    }
    return Math.min(Math.max(value, min), max);
}

function borderXOf(rect: NodeRect, side: RouteSide): number {
    return side === "left" ? rect.x : rect.x + rect.width;
}

function centerXOf(rect: NodeRect): number {
    return rect.x + rect.width / 2;
}

function centerYOf(rect: NodeRect): number {
    return rect.y + rect.height / 2;
}

function hasVerticalOverlap(a: NodeRect, b: NodeRect): boolean {
    return a.y < b.y + b.height && b.y < a.y + a.height;
}

function isHorizontalRunBlocked({
    minX,
    maxX,
    y,
    obstacles,
}: {
    minX: number;
    maxX: number;
    y: number;
    obstacles: NodeRect[];
}): boolean {
    return obstacles.some(
        (rect) =>
            rect.x < maxX &&
            rect.x + rect.width > minX &&
            rect.y < y &&
            rect.y + rect.height > y,
    );
}

interface RouteContext {
    source: NodeRect;
    target: NodeRect;
    obstacles: NodeRect[];
}

/**
 * Same-row pair (their vertical extents overlap). The edge's label rides a
 * horizontal run on the band centerline. A run blocked by an intervening node
 * drops into a lane below the row; a clear run draws as a straight line.
 */
function routeSameRow({
    source,
    target,
    obstacles,
}: RouteContext): CanvasPoint[] {
    const sourceSide: RouteSide =
        centerXOf(target) >= centerXOf(source) ? "right" : "left";
    const targetSide: RouteSide = sourceSide === "right" ? "left" : "right";
    const sx = borderXOf(source, sourceSide);
    const tx = borderXOf(target, targetSide);
    const minX = Math.min(sx, tx);
    const maxX = Math.max(sx, tx);

    const bandTop = Math.max(source.y, target.y);
    const bandBottom = Math.min(
        source.y + source.height,
        target.y + target.height,
    );
    let laneY = (bandTop + bandBottom) / 2;
    if (isHorizontalRunBlocked({ minX, maxX, y: laneY, obstacles })) {
        laneY =
            Math.max(source.y + source.height, target.y + target.height) +
            ROW_CLEARANCE;
    }

    // Endpoints must attach within the node's height; the run may sit outside
    // it, joined by a short vertical jog.
    const attachY = clamp(laneY, bandTop + EDGE_INSET, bandBottom - EDGE_INSET);
    if (
        laneY === attachY &&
        !isHorizontalRunBlocked({ minX, maxX, y: laneY, obstacles })
    ) {
        return [
            { x: sx, y: laneY },
            { x: tx, y: laneY },
        ];
    }

    const out = sourceSide === "right" ? 1 : -1;
    const sStub = sx + out * ELBOW;
    const tStub = tx - out * ELBOW;
    return [
        { x: sx, y: attachY },
        { x: sStub, y: attachY },
        { x: sStub, y: laneY },
        { x: tStub, y: laneY },
        { x: tStub, y: attachY },
        { x: tx, y: attachY },
    ];
}

/**
 * Same-column reference loop (endpoints roughly vertically aligned): a short
 * C on whichever side has room, hugging just outside the borders.
 */
function chooseLoopSide(
    source: NodeRect,
    target: NodeRect,
    obstacles: NodeRect[],
): RouteSide {
    const runTop = Math.min(source.y, target.y);
    const runBottom = Math.max(
        source.y + source.height,
        target.y + target.height,
    );
    const rightEdge = Math.max(
        borderXOf(source, "right"),
        borderXOf(target, "right"),
    );
    const isRightBlocked = obstacles.some(
        (rect) =>
            rect.x < rightEdge + LOCAL_LANE_GAP * 2 &&
            rect.x + rect.width > rightEdge &&
            rect.y < runBottom &&
            rect.y + rect.height > runTop,
    );
    return isRightBlocked ? "left" : "right";
}

/**
 * Stacked pair (different rows): step between the two facing borders through a
 * local vertical lane sitting between their columns; if the columns overlap,
 * loop just outside one side instead.
 */
function routeAcrossRows({
    source,
    target,
    obstacles,
}: RouteContext): CanvasPoint[] {
    const sCx = centerXOf(source);
    const tCx = centerXOf(target);
    const horizontalGap =
        Math.abs(tCx - sCx) - (source.width + target.width) / 2;

    if (horizontalGap > MIN_COLUMN_GAP) {
        const sourceSide: RouteSide = tCx >= sCx ? "right" : "left";
        const targetSide: RouteSide = tCx >= sCx ? "left" : "right";
        const sx = borderXOf(source, sourceSide);
        const tx = borderXOf(target, targetSide);
        const laneX = (sx + tx) / 2;
        const sy = centerYOf(source);
        const ty = centerYOf(target);
        return [
            { x: sx, y: sy },
            { x: laneX, y: sy },
            { x: laneX, y: ty },
            { x: tx, y: ty },
        ];
    }

    const side = chooseLoopSide(source, target, obstacles);
    const sx = borderXOf(source, side);
    const tx = borderXOf(target, side);
    const laneX =
        side === "right"
            ? Math.max(sx, tx) + LOCAL_LANE_GAP
            : Math.min(sx, tx) - LOCAL_LANE_GAP;
    return [
        { x: sx, y: centerYOf(source) },
        { x: laneX, y: centerYOf(source) },
        { x: laneX, y: centerYOf(target) },
        { x: tx, y: centerYOf(target) },
    ];
}

interface RouteReferenceEdgesParams {
    nodes: OwnershipFlowNode[];
    edges: OwnershipFlowEdge[];
}

/**
 * Routes reference (non-ownership) edges DIRECTLY between the two entities'
 * facing left/right borders — never around the diagram. Each unordered entity
 * pair carries a single (already bundled) reference connector. Same-row pairs
 * get a straight horizontal line (or a small local dip past a blocking node);
 * stacked pairs step through a short local lane between their columns. Ownership
 * edges are skipped (laid out by ELK).
 */
export function routeReferenceEdges({
    nodes,
    edges,
}: RouteReferenceEdgesParams): Map<string, CanvasPoint[]> {
    const rects = getAbsoluteNodeRects(nodes);
    // Group boundaries are not obstacles: edges cross them to reach members.
    const entityRects = nodes
        .filter((node) => node.type === "entityNode")
        .map((node) => rects.get(node.id))
        .filter((rect): rect is NodeRect => rect !== undefined);

    const routed = new Map<string, CanvasPoint[]>();
    for (const edge of edges) {
        if (!isReferenceEdge(edge)) {
            continue;
        }
        const source = rects.get(edge.source);
        const target = rects.get(edge.target);
        if (!source || !target) {
            continue;
        }
        const context: RouteContext = {
            source,
            target,
            obstacles: entityRects.filter(
                (rect) => rect !== source && rect !== target,
            ),
        };
        routed.set(
            edge.id,
            hasVerticalOverlap(source, target)
                ? routeSameRow(context)
                : routeAcrossRows(context),
        );
    }

    return routed;
}

/**
 * Builds an SVG path through orthogonal points with rounded corners.
 */
export function pointsToRoundedPath(
    points: CanvasPoint[],
    radius: number,
): string {
    if (points.length === 0) {
        return "";
    }
    if (points.length === 1) {
        return `M ${points[0].x},${points[0].y}`;
    }

    let path = `M ${points[0].x},${points[0].y}`;
    for (let i = 1; i < points.length - 1; i += 1) {
        const previous = points[i - 1];
        const corner = points[i];
        const next = points[i + 1];

        const inLength = Math.hypot(
            corner.x - previous.x,
            corner.y - previous.y,
        );
        const outLength = Math.hypot(next.x - corner.x, next.y - corner.y);
        const cornerRadius = Math.min(radius, inLength / 2, outLength / 2);

        const inX =
            corner.x - (cornerRadius * Math.sign(corner.x - previous.x) || 0);
        const inY =
            corner.y - (cornerRadius * Math.sign(corner.y - previous.y) || 0);
        const outX =
            corner.x + (cornerRadius * Math.sign(next.x - corner.x) || 0);
        const outY =
            corner.y + (cornerRadius * Math.sign(next.y - corner.y) || 0);

        path += ` L ${inX},${inY} Q ${corner.x},${corner.y} ${outX},${outY}`;
    }
    const last = points[points.length - 1];
    path += ` L ${last.x},${last.y}`;
    return path;
}

/**
 * Label anchor: midpoint of the longest segment, where there is the most
 * room (the gutter run for reference edges, the trunk for ownership edges).
 */
export function labelAnchorForPoints(points: CanvasPoint[]): CanvasPoint {
    if (points.length === 0) {
        return { x: 0, y: 0 };
    }
    if (points.length === 1) {
        return points[0];
    }

    let bestStart = points[0];
    let bestEnd = points[1];
    let bestLength = -1;
    for (let i = 0; i < points.length - 1; i += 1) {
        const length = Math.hypot(
            points[i + 1].x - points[i].x,
            points[i + 1].y - points[i].y,
        );
        if (length > bestLength) {
            bestLength = length;
            bestStart = points[i];
            bestEnd = points[i + 1];
        }
    }
    return {
        x: (bestStart.x + bestEnd.x) / 2,
        y: (bestStart.y + bestEnd.y) / 2,
    };
}

interface ResolveLabelAnchorsParams {
    anchorsByEdgeId: Map<string, CanvasPoint>;
    nodes: OwnershipFlowNode[];
    // Routed points per edge. When supplied, a colliding label slides ALONG
    // its edge's longest segment (so it never floats off the edge) instead of
    // being pushed straight down.
    pointsByEdgeId?: Map<string, CanvasPoint[]>;
}

function intersectsRect(anchor: CanvasPoint, rect: NodeRect): boolean {
    return (
        anchor.x + LABEL_WIDTH / 2 > rect.x &&
        anchor.x - LABEL_WIDTH / 2 < rect.x + rect.width &&
        anchor.y + LABEL_HEIGHT / 2 > rect.y &&
        anchor.y - LABEL_HEIGHT / 2 < rect.y + rect.height
    );
}

interface Segment {
    start: CanvasPoint;
    end: CanvasPoint;
    length: number;
}

function segmentsByLengthDesc(points: CanvasPoint[]): Segment[] {
    const segments: Segment[] = [];
    for (let i = 0; i < points.length - 1; i += 1) {
        const start = points[i];
        const end = points[i + 1];
        segments.push({
            start,
            end,
            length: Math.hypot(end.x - start.x, end.y - start.y),
        });
    }
    return segments.sort((a, b) => b.length - a.length);
}

/**
 * Tries to seat a label on a single segment without colliding: starts at the
 * segment midpoint and slides outward along it (clamped so the label box stays
 * on the segment). Returns null when no clear spot fits on this segment.
 */
function tryPlaceOnSegment(
    segment: Segment,
    collides: (candidate: CanvasPoint) => boolean,
): CanvasPoint | null {
    const midpoint = {
        x: (segment.start.x + segment.end.x) / 2,
        y: (segment.start.y + segment.end.y) / 2,
    };
    if (segment.length === 0) {
        return collides(midpoint) ? null : midpoint;
    }
    const unitX = (segment.end.x - segment.start.x) / segment.length;
    const unitY = (segment.end.y - segment.start.y) / segment.length;
    const maxOffset = segment.length / 2 - LABEL_HEIGHT / 2;
    for (let step = 0; step * LABEL_HEIGHT <= maxOffset; step += 1) {
        const directions = step === 0 ? [1] : [1, -1];
        for (const direction of directions) {
            const offset = direction * step * LABEL_HEIGHT;
            const candidate = {
                x: midpoint.x + unitX * offset,
                y: midpoint.y + unitY * offset,
            };
            if (!collides(candidate)) {
                return candidate;
            }
        }
    }
    return null;
}

/**
 * Slides a label along its routed edge — trying every segment longest-first, so
 * e.g. a label can drop onto the vertical lane to stack with a neighbor instead
 * of overlapping it. Falls back to the anchor when no segment has room.
 */
function placeLabelOnEdge(
    anchor: CanvasPoint,
    points: CanvasPoint[],
    collides: (candidate: CanvasPoint) => boolean,
): CanvasPoint {
    const placedOnSegment = segmentsByLengthDesc(points)
        .map((segment) => tryPlaceOnSegment(segment, collides))
        .find((point): point is CanvasPoint => point !== null);
    return placedOnSegment ?? anchor;
}

/**
 * Nudges a label straight down until it clears collisions (fallback used when
 * the edge's routed points aren't available).
 */
function nudgeLabelDown(
    anchor: CanvasPoint,
    collides: (candidate: CanvasPoint) => boolean,
): CanvasPoint {
    const nudged = { ...anchor };
    for (let i = 0; i < MAX_PUSH_ITERATIONS && collides(nudged); i += 1) {
        nudged.y += LABEL_HEIGHT;
    }
    return nudged;
}

/**
 * Places each edge label so it overlaps neither nodes nor labels placed before
 * it (insertion order = edge order, deterministic). A non-colliding label stays
 * at its provided anchor. A colliding one slides along its edge (or nudges down
 * when routed points aren't supplied).
 */
export function resolveLabelAnchors({
    anchorsByEdgeId,
    nodes,
    pointsByEdgeId,
}: ResolveLabelAnchorsParams): Map<string, CanvasPoint> {
    const rects = getAbsoluteNodeRects(nodes);
    const nodeRects = nodes
        .filter((node) => node.type === "entityNode")
        .map((node) => rects.get(node.id))
        .filter((rect): rect is NodeRect => rect !== undefined);
    const placed: CanvasPoint[] = [];
    const resolved = new Map<string, CanvasPoint>();

    const collidesAt = (candidate: CanvasPoint): boolean =>
        nodeRects.some((rect) => intersectsRect(candidate, rect)) ||
        placed.some(
            (other) =>
                Math.abs(other.x - candidate.x) < LABEL_WIDTH &&
                Math.abs(other.y - candidate.y) < LABEL_HEIGHT,
        );

    for (const [edgeId, anchor] of anchorsByEdgeId) {
        const points = pointsByEdgeId?.get(edgeId);
        let candidate = anchor;
        if (collidesAt(anchor)) {
            candidate = points
                ? placeLabelOnEdge(anchor, points, collidesAt)
                : nudgeLabelDown(anchor, collidesAt);
        }
        placed.push(candidate);
        resolved.set(edgeId, candidate);
    }

    return resolved;
}

interface ContentBounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

interface GroupGrowth {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

interface ExpandGroupNodesParams {
    nodes: OwnershipFlowNode[];
    edges: OwnershipFlowEdge[];
}

/**
 * Grows each consolidation boundary so it contains every intra-group
 * relationship (both endpoints are members) — its routed path AND its label —
 * plus padding. Reference edges loop around the diagram side and would
 * otherwise spill outside the dashed box. Members are shifted to compensate
 * for the moved group origin so they stay put visually; edges live in absolute
 * coordinates and don't move.
 */
export function expandGroupNodesForEdgeContent({
    nodes,
    edges,
}: ExpandGroupNodesParams): OwnershipFlowNode[] {
    const membersByGroupId = new Map<string, Set<string>>();
    for (const node of nodes) {
        if (node.type === "entityNode" && node.parentId) {
            const members = membersByGroupId.get(node.parentId) ?? new Set();
            members.add(node.id);
            membersByGroupId.set(node.parentId, members);
        }
    }

    const growthByGroupId = new Map<string, GroupGrowth>();
    for (const node of nodes) {
        if (node.type !== "groupNode") {
            continue;
        }
        const members = membersByGroupId.get(node.id);
        if (!members || members.size === 0) {
            continue;
        }
        let bounds: ContentBounds | null = null;
        const include = (x: number, y: number) => {
            bounds = bounds
                ? {
                      minX: Math.min(bounds.minX, x),
                      minY: Math.min(bounds.minY, y),
                      maxX: Math.max(bounds.maxX, x),
                      maxY: Math.max(bounds.maxY, y),
                  }
                : { minX: x, minY: y, maxX: x, maxY: y };
        };
        for (const edge of edges) {
            if (!members.has(edge.source) || !members.has(edge.target)) {
                continue;
            }
            for (const point of edge.data?.points ?? []) {
                include(point.x, point.y);
            }
            const anchor = edge.data?.labelAnchor;
            if (anchor) {
                include(
                    anchor.x - LABEL_WIDTH / 2,
                    anchor.y - LABEL_HEIGHT / 2,
                );
                include(
                    anchor.x + LABEL_WIDTH / 2,
                    anchor.y + LABEL_HEIGHT / 2,
                );
            }
        }
        if (!bounds) {
            continue;
        }
        const content: ContentBounds = bounds;
        const groupX = node.position.x;
        const groupY = node.position.y;
        const groupRight = groupX + (node.width ?? 0);
        const groupBottom = groupY + (node.height ?? 0);
        const growth: GroupGrowth = {
            left: Math.max(0, groupX - (content.minX - GROUP_CONTENT_PADDING)),
            top: Math.max(0, groupY - (content.minY - GROUP_CONTENT_PADDING)),
            right: Math.max(
                0,
                content.maxX + GROUP_CONTENT_PADDING - groupRight,
            ),
            bottom: Math.max(
                0,
                content.maxY + GROUP_CONTENT_PADDING - groupBottom,
            ),
        };
        if (growth.left || growth.top || growth.right || growth.bottom) {
            growthByGroupId.set(node.id, growth);
        }
    }

    if (growthByGroupId.size === 0) {
        return nodes;
    }

    return nodes.map((node): OwnershipFlowNode => {
        if (node.type === "groupNode") {
            const growth = growthByGroupId.get(node.id);
            if (!growth) {
                return node;
            }
            return {
                ...node,
                position: {
                    x: node.position.x - growth.left,
                    y: node.position.y - growth.top,
                },
                width: (node.width ?? 0) + growth.left + growth.right,
                height: (node.height ?? 0) + growth.top + growth.bottom,
            };
        }
        if (node.type === "entityNode" && node.parentId) {
            const growth = growthByGroupId.get(node.parentId);
            if (!growth) {
                return node;
            }
            return {
                ...node,
                position: {
                    x: node.position.x + growth.left,
                    y: node.position.y + growth.top,
                },
            };
        }
        return node;
    });
}
